import { inject, type InjectionKey, type ModelRef, type Ref, type UnwrapNestedRefs } from 'vue'
import { type ComponentExposed } from 'vue-component-type-helpers'

import { type Model as CollapseModel, type Props as CollapseProps } from '../components/Collapse/VCollapse.vue'
import { type QuestionType } from '../components/FormBuilder/useBlueprint.ts'
import { type Option } from '../components/Select/VOption.vue'
import { type Props as OptionGroupProps } from '../components/Select/VOptionGroup.vue'
import { type Props as StepsProps } from '../components/Steps/VSteps.vue'
import { type TableColumn } from '../components/Table/VTableColumn.vue'
import { type Props as TableKeyValueProps } from '../components/Table/VTableKeyValue.vue'
import { type Props as CheckboxGroupProps } from '../components/VCheckboxGroup.vue'
import { type Props as FormProps } from '../components/VForm.vue'
import type VInput from '../components/VInput.vue'
import type VPopper from '../components/VPopper.vue'
import { type Props as RadioGroupProps } from '../components/VRadioGroup.vue'
import { type useForm } from '../composables/useForm.ts'
import { type SomeOtherString, type VFormTrigger } from '../types.ts'

export function injectRequired<T>(key: string | InjectionKey<T>): NonNullable<T> {
  const injection = inject(key)

  if (!injection) {
    throw Error(`Injection ${String(key)} is required`)
  }

  return injection
}

export const $blueprintQuestionTypesKey = Symbol('VBlueprintQuestionTypes') as InjectionKey<(QuestionType | SomeOtherString)[]>

export const $checkboxGroupKey = Symbol('VCheckboxGroup') as InjectionKey<{
  props: CheckboxGroupProps
  model: ModelRef<(string | number)[]>
  input: (value: string | number) => void
}>

export const $collapseKey = Symbol('VCollapse') as InjectionKey<{
  props: CollapseProps
  model: ModelRef<CollapseModel>
}>

export const $dialogKey = Symbol('VDialog') as InjectionKey<{
  visible: ModelRef<boolean>
  isWrapperVisible: Ref<boolean>
}>

export const $formKey = Symbol('VForm') as InjectionKey<{
  props: FormProps
  form: ReturnType<typeof useForm>
  submit: () => void
  validateOneProp: (prop: string, trigger?: VFormTrigger) => void
}>

export const $optionGroupKey = Symbol('VOptionGroup') as InjectionKey<{
  props: OptionGroupProps
  options: Record<number, UnwrapNestedRefs<Option>>
}>

export const $radioGroupKey = Symbol('VRadio') as InjectionKey<{
  props: RadioGroupProps
  input: (value: any) => void
}>

export const $selectKey = Symbol('VRadio') as InjectionKey<{
  multiple: boolean
  multipleLimit: number
  prop: string
  filterable: boolean
  ignoreClickOnSelectedOption: boolean
  noDropdown: boolean
  model: ModelRef<any>
  activeOption: Ref<undefined | UnwrapNestedRefs<Option>>
  filterValue: Ref<string>
  options: Record<number, UnwrapNestedRefs<Option>>
  popper: Ref<InstanceType<typeof VPopper> | null>
  input: Ref<ComponentExposed<typeof VInput> | null>
  focus: () => void
  emit: (event: any, value: any) => void
  valueMatch: (value1: any, value2: any) => boolean
}>

export const $stepsKey = Symbol('VSteps') as InjectionKey<{
  props: StepsProps
  model: ModelRef<number>
  steps: Ref<number[]>
}>

export const $tableKey = Symbol('VTable') as InjectionKey<{
  tableColumns: Ref<TableColumn[]>
}>
export const $tableKeyValueKey = Symbol('VTableKeyValue') as InjectionKey<TableKeyValueProps>
